import React, { useMemo } from 'react';
import { SkeletonItem } from '@atlaskit/side-navigation';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { ForgeScreenEvent } from '@atlassian/jira-forge-ui-analytics/src/common/ui/index.tsx';
import { TriggerAnalyticsWrapper } from '@atlassian/jira-forge-ui-analytics/src/ui/project-settings-page/index.tsx';
import { PROJECT_SETTINGS_PAGE_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { ForgeItem } from '@atlassian/jira-navigation-apps-sidebar-forge/src/common/ui/forge-item/index.tsx';
import type { ForgeProjectSettingsItem } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project-settings/index.tsx';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import type { App } from '@atlassian/jira-project-settings-apps-connect-apps/src/model/connect-apps/types.tsx';
import type { Props } from './types.tsx';

export const EcosystemApps = ({ baseSettingsUrl = '', apps }: Props) => {
	const forgeApps = useMemo(() => apps.forge || [], [apps.forge]);
	const connectApps = useMemo(() => apps.connect || [], [apps.connect]);

	const sortedApps = useMemo(
		() =>
			[
				...forgeApps.map((app) => ({ type: 'forge', name: app.name, app })),
				...connectApps.map((app) => ({ type: 'connect', name: app.title, app })),
			].sort((a, b) => a.name.localeCompare(b.name)),
		[connectApps, forgeApps],
	);

	if (apps.forge == null || apps.connect == null) return <SkeletonItem />;

	return (
		<>
			{sortedApps.map(({ type, app }) => {
				if (type === 'forge') {
					// @ts-expect-error - TS2322 - Type 'App | App' is not assignable to type 'ForgeProjectSettingsItem'.
					const item: ForgeProjectSettingsItem = app;
					return <ForgeItem key={app.id} {...item} showIcon={false} nestedMenuActive={false} />;
				}
				// @ts-expect-error - TS2322 - Type 'App | App' is not assignable to type 'App'.
				const connectApp: App = app;
				return (
					<MenuItem
						analytics={{ itemId: 'connect-app' }}
						key={connectApp.id}
						href={`${baseSettingsUrl}/apps/${connectApp.id}`}
						selectedOn={matchUrlPathname()}
					>
						{connectApp.title}
					</MenuItem>
				);
			})}
			{forgeApps.length ? (
				<ForgeScreenEvent
					attributes={{
						moduleType: PROJECT_SETTINGS_PAGE_MODULE,
					}}
				/>
			) : null}
		</>
	);
};

export const EcosystemAppsMenu = (props: Props) => {
	const { data: project } = useProject();
	const projectKey = (project && project.key) || ''; // flow
	const baseSettingsUrl = getNextgenSettingsUrl(SERVICE_DESK_PROJECT, projectKey);

	return (
		<TriggerAnalyticsWrapper>
			<EcosystemApps baseSettingsUrl={baseSettingsUrl} {...props} />
		</TriggerAnalyticsWrapper>
	);
};
